import { useEffect } from 'react';

import { envConfig } from '#constants/envConfig';

/**
 * Wires Android's system back button in the native shell. No-op / tree-shaken on web.
 *
 * Without a listener the button does *nothing at all* on the first screen: the
 * @capacitor/app plugin installs an always-enabled OnBackPressedCallback, so it goes
 * back through history while there is history and then swallows the event — the
 * activity is never finished and the app cannot be closed with the button. That reads
 * as a frozen app, so Android users hit it early and loudly.
 *
 * Order of handling, matching what people expect from a native app:
 *  1. an open dialog/sheet closes;
 *  2. otherwise navigate back through history;
 *  3. on the first screen minimise (like Home), NOT exit — exiting would make the
 *     next launch a cold start, and Capacitor cannot restore the previous state.
 *
 * Overlays are closed by dispatching Escape rather than through a registry: Ark UI
 * (Chakra 3) dismisses the topmost open layer on a document-level Escape, and the
 * project has no global store of open dialogs to consult.
 *
 * Mount once, high in the tree.
 */
export const useNativeBackButton = () => {
  useEffect(() => {
    if (!envConfig.IS_NATIVE) {
      return;
    }

    let remove: (() => void) | undefined;
    let active = true;

    (async () => {
      const { App } = await import('@capacitor/app');

      const handle = await App.addListener('backButton', ({ canGoBack }) => {
        if (closeTopmostOverlay()) {
          return;
        }

        if (canGoBack) {
          window.history.back();
          return;
        }

        void App.minimizeApp();
      });

      if (active) {
        remove = () => handle.remove();
      } else {
        handle.remove();
      }
    })();

    return () => {
      active = false;
      remove?.();
    };
  }, []);
};

/**
 * Returns true when something dismissable was on screen, so the back press is spent
 * on closing it instead of navigating away and leaving the overlay orphaned.
 */
const closeTopmostOverlay = (): boolean => {
  const open = document.querySelector(
    '[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]'
  );

  if (!open) {
    return false;
  }

  document.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape', bubbles: true }));
  return true;
};
